import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TEXT_SCALE_STORAGE_PREFIX = 'ufo:text-scale:';
const TEXT_SCALE_OPTIONS = [0.9, 1, 1.1, 1.2, 1.3] as const;
const DEFAULT_TEXT_SCALE = 1;

function storageKey(pathname: string) {
  return `${TEXT_SCALE_STORAGE_PREFIX}${pathname}`;
}

function nearestScale(value: number) {
  return TEXT_SCALE_OPTIONS.reduce((closest, option) => (
    Math.abs(option - value) < Math.abs(closest - value) ? option : closest
  ), DEFAULT_TEXT_SCALE);
}

function readSavedScale(pathname: string) {
  try {
    const saved = window.localStorage.getItem(storageKey(pathname));
    if (!saved) return DEFAULT_TEXT_SCALE;
    const parsed = Number(saved);
    return Number.isFinite(parsed) ? nearestScale(parsed) : DEFAULT_TEXT_SCALE;
  } catch {
    return DEFAULT_TEXT_SCALE;
  }
}

function applyTextScale(scale: number) {
  document.documentElement.style.setProperty('--page-text-scale', String(scale));
}

function saveTextScale(pathname: string, scale: number) {
  try {
    window.localStorage.setItem(storageKey(pathname), String(scale));
  } catch {
    // Preference persistence is best-effort; the control still works for this session.
  }
}

export function TextSizeToggle() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [scaleByPath, setScaleByPath] = useState<Record<string, number>>({});
  const savedScale = useMemo(() => readSavedScale(pathname), [pathname]);
  const scale = scaleByPath[pathname] ?? savedScale;
  const scaleIndex = TEXT_SCALE_OPTIONS.indexOf(nearestScale(scale));

  useEffect(() => {
    applyTextScale(scale);
  }, [scale]);

  function updateScale(direction: -1 | 1) {
    setScaleByPath((currentScales) => {
      const currentScale = currentScales[pathname] ?? savedScale;
      const currentIndex = TEXT_SCALE_OPTIONS.indexOf(nearestScale(currentScale));
      const nextIndex = Math.min(
        TEXT_SCALE_OPTIONS.length - 1,
        Math.max(0, currentIndex + direction),
      );
      const nextScale = TEXT_SCALE_OPTIONS[nextIndex];
      applyTextScale(nextScale);
      saveTextScale(pathname, nextScale);
      return { ...currentScales, [pathname]: nextScale };
    });
  }

  return (
    <div className="text-size-toggle" role="group" aria-label={t('common.textSizeControls')}>
      <button
        type="button"
        className="ctrl-btn ctrl-btn--text-size"
        onClick={() => updateScale(-1)}
        disabled={scaleIndex === 0}
        aria-label={t('common.decreaseTextSize')}
      >
        <span className="text-size-label-wide">{t('common.textMinus')}</span>
        <span className="text-size-label-short" aria-hidden="true">T-</span>
      </button>
      <span className="text-size-value" aria-live="polite">
        {Math.round(scale * 100)}%
      </span>
      <button
        type="button"
        className="ctrl-btn ctrl-btn--text-size"
        onClick={() => updateScale(1)}
        disabled={scaleIndex === TEXT_SCALE_OPTIONS.length - 1}
        aria-label={t('common.increaseTextSize')}
      >
        <span className="text-size-label-wide">{t('common.textPlus')}</span>
        <span className="text-size-label-short" aria-hidden="true">T+</span>
      </button>
    </div>
  );
}
